import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { applicationInsights } from '../../../ApplicationInsights';
import { IGeneralContractor, IGeneralContractorSummary, UsersApi } from '@apply/api/src/api/usersApi';
import { SearchResponse } from '@apply/api/src/api/bfiApi';
import { updateCachedItem } from '@apply/api';
import { handleAdminError } from '../../../common/SharedFunctions';

enum QueryKeys {
    GetGeneralContractor = 'general-contractor',
    GetGeneralContractors = 'general-contractors',
}

const usersApi = new UsersApi(applicationInsights);

export const useGeneralContractorQuery = (contractorId: number | undefined) =>
    useQuery({
        queryKey: [QueryKeys.GetGeneralContractor, contractorId],
        enabled: !!contractorId,
        queryFn: async () => {
            if (!contractorId) return undefined;

            const response = await usersApi.getGeneralContractorUser(contractorId);

            if (response.statusCode === 200) return response.result;

            handleAdminError(new Error('Failed retrieve general contractors.'));

            return undefined;
        },
    });

export const useGeneralContractorsQuery = (searchText: string) =>
    useQuery({
        queryKey: [QueryKeys.GetGeneralContractors, searchText],
        queryFn: async () => {
            const response: SearchResponse<IGeneralContractorSummary[]> = await usersApi.getGeneralContractorUsers(searchText);

            if (response.statusCode === 200) return response.result.data;

            handleAdminError(new Error('Failed retrieve general contractors.'));

            return [];
        },
        staleTime: 0,
    });

export const useUpdateGeneralContractor = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (contractor: IGeneralContractor) => {
            const response = await usersApi.saveGeneralContractor(contractor);

            if (response.statusCode === 200) return response.result;

            handleAdminError(new Error('Error saving general contractor.'));

            return contractor;
        },
        onSuccess: (contractor: IGeneralContractor) => {
            queryClient.setQueryData([QueryKeys.GetGeneralContractor, contractor.id], contractor);
            queryClient.setQueryData([QueryKeys.GetGeneralContractors], (contractors: IGeneralContractor[] = []) =>
                updateCachedItem(contractors, contractor, (u) => u.id)
            );
        },
    });
};
